import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EvergreenPaywall.module.scss"

/**
 * EvergreenPaywall (`/evergreen`) — a self-contained EXPIRATION / RENEWAL paywall
 * for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/evergreen.md
 * Six prior expiration concepts exist — Deep Freeze (cold fear / ice meter),
 * Unbroken (warm calendar flood), Ahead (forking future-self road), Keystone
 * (masonry arch drag), Keep Posted (Solari split-flap lever) and Patchwork (run
 * the thread on a quilt). This is none of them on any axis:
 *   - Layout:      a RADIAL almanac "Wheel of the Year" — twelve month photo-leaves
 *                  around a central living tree, one annual growth ring. No prior
 *                  paywall used a radial / circular / mandala skeleton.
 *   - Interaction: a rotary SEASONS DIAL — turn the season hand around the wheel
 *                  and each month it sweeps past unfurls into leaf. (First rotary
 *                  mechanic; priors used scrub / drag-drop / toggle / hold / flip /
 *                  toss / paint / pull-lever / pull-thread / page-turn.)
 *   - Register:    living / seasonal-continuity / nurturing-calm — "keep it
 *                  evergreen." Not fear, streak-pride, hopeful-road, gravity,
 *                  mechanical or textile.
 *   - Art:         LIGHT warm botanical almanac; brand blue = the tended / evergreen
 *                  accent, sage + amber = foliage / the loose season bud. Photos are
 *                  ALWAYS full colour — only the foliage (the sign of automatic
 *                  tending) changes (deliberately NOT a grey→colour bloom).
 *   - Value frame: CONTINUITY of automatic care for RENEWAL via cyclical +
 *                  goal-gradient completion — "your library, kept green all year."
 *
 * Honest & App Store compliant: renewal keeps the real Premium bundle running
 * (automatic full-resolution backup, private/encrypted, organised, in sync on
 * every device). Nothing is deleted on expiration and the copy says so outright.
 * No fake countdown / scarcity; savings are exact yearly-vs-monthly arithmetic.
 * Yearly is the featured, default plan and carries the most prominent price;
 * monthly is small and secondary. Photo tiles are bundled Unsplash demo memories.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// ── Wheel geometry (fixed px so pointer angles map cleanly) ─────────────────
const BOX = 328 // dial box, px (square)
const C = BOX / 2 // centre
const NODE_R = 118 // radius of the month-node ring
const RING_R = 118 // the drawn "vine" circle radius
const CIRC = 2 * Math.PI * RING_R // circumference for the closing arc

// The twelve months of memories the tree is grown from. Photos are bundled
// Unsplash demo memories reused from the app's memory-photo set (no egress).
const MONTHS: { m: string; src: string; tag: string }[] = [
  { m: "Jan", src: "/byheart/m01.jpg", tag: "First snow" },
  { m: "Feb", src: "/byheart/m02.jpg", tag: "Cosy morning" },
  { m: "Mar", src: "/byheart/m03.jpg", tag: "First bloom" },
  { m: "Apr", src: "/byheart/m04.jpg", tag: "Puddle jumps" },
  { m: "May", src: "/byheart/m05.jpg", tag: "Garden days" },
  { m: "Jun", src: "/byheart/m06.jpg", tag: "Long light" },
  { m: "Jul", src: "/byheart/m07.jpg", tag: "By the water" },
  { m: "Aug", src: "/byheart/m08.jpg", tag: "Road trip" },
  { m: "Sep", src: "/byheart/m09.jpg", tag: "Back to it" },
  { m: "Oct", src: "/byheart/m16.jpg", tag: "Leaf walks" },
  { m: "Nov", src: "/byheart/m11.jpg", tag: "Gathered in" },
  { m: "Dec", src: "/byheart/m14.jpg", tag: "The whole year" },
]

// Angle (deg) of month i on the ring — 0 = 12 o'clock, clockwise.
const nodeAngle = (i: number) => -90 + i * 30
const polar = (angleDeg: number, r: number) => {
  const a = (angleDeg * Math.PI) / 180
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) }
}

// Pointer angle measured clockwise from 12 o'clock, in degrees [0,360).
const angleFromTop = (dx: number, dy: number) => {
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI
  if (deg < 0) deg += 360
  return deg
}
const shortestDiff = (a: number, b: number) => {
  let d = a - b
  while (d > 180) d -= 360
  while (d < -180) d += 360
  return d
}

// A small leaf sprig, drawn in SVG. Rotated per node so leaves fan outward.
const Leaf = ({ scale = 1 }: { scale?: number }) => (
  <svg viewBox="0 0 24 24" width={18 * scale} height={18 * scale} aria-hidden>
    <path
      d="M21 3C11 3 4 8 4 16c0 2 .6 3.6 1.5 4.9C8 15 12 12 18 10.5 12.5 13 8.7 16.4 7 21c8 0 14-5 14-13 0-2-.2-3.6-.6-5z"
      fill="currentColor"
    />
  </svg>
)

const Tick = ({ size = 13 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EvergreenPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  // progress 0..1 = how far around the year the season hand has been turned.
  const [progress, setProgress] = useState(0)
  const [touched, setTouched] = useState(false)

  const wrapRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const lastAngleRef = useRef(0)
  const demoRef = useRef<number | null>(null)

  // Yearly can be turned around the whole wheel; monthly holds one month green.
  const monthsInLeaf =
    plan === "monthly" ? 1 : Math.min(12, 1 + Math.floor(progress * 12 + 1e-9))
  const arcFraction = plan === "monthly" ? 1 / 12 : progress
  const handAngle = plan === "monthly" ? 0 : progress * 360
  const evergreen = plan === "yearly" && monthsInLeaf >= 12

  const cancelDemo = useCallback(() => {
    if (demoRef.current != null) {
      cancelAnimationFrame(demoRef.current)
      demoRef.current = null
    }
  }, [])

  // Auto-demo: turn the wheel ~a third of the way on load, then hand it over.
  useEffect(() => {
    if (reduceMotion) {
      setProgress(0.34)
      return
    }
    let raf = 0
    const start = performance.now()
    const DURATION = 1500
    const TARGET = 0.34
    const tick = (now: number) => {
      const t = clamp((now - start) / DURATION, 0, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setProgress(TARGET * eased)
      if (t < 1) {
        raf = requestAnimationFrame(tick)
        demoRef.current = raf
      } else {
        demoRef.current = null
      }
    }
    // brief beat before it starts turning
    const startTimer = window.setTimeout(() => {
      raf = requestAnimationFrame(tick)
      demoRef.current = raf
    }, 450)
    return () => {
      window.clearTimeout(startTimer)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [reduceMotion])

  const setFromPointer = (clientX: number, clientY: number) => {
    const el = wrapRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const dx = clientX - (r.left + r.width / 2)
    const dy = clientY - (r.top + r.height / 2)
    const ang = angleFromTop(dx, dy)
    const delta = shortestDiff(ang, lastAngleRef.current)
    lastAngleRef.current = ang
    setProgress((p) => clamp(p + delta / 360, 0, 1))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (plan === "monthly") return
    cancelDemo()
    setTouched(true)
    draggingRef.current = true
    const el = wrapRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      lastAngleRef.current = angleFromTop(
        e.clientX - (r.left + r.width / 2),
        e.clientY - (r.top + r.height / 2),
      )
      el.setPointerCapture?.(e.pointerId)
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    setFromPointer(e.clientX, e.clientY)
  }
  const endDrag = () => {
    draggingRef.current = false
  }

  // Tap a month node → turn the wheel to that month.
  const goToMonth = (i: number) => {
    if (plan === "monthly") return
    cancelDemo()
    setTouched(true)
    setProgress(clamp(i / 12, 0, 1))
  }

  const fillWholeYear = () => {
    cancelDemo()
    setTouched(true)
    setPlan("yearly")
    setProgress(1)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (plan === "monthly") return
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      cancelDemo()
      setTouched(true)
      setProgress((p) => clamp(p + 1 / 12, 0, 1))
      e.preventDefault()
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      cancelDemo()
      setTouched(true)
      setProgress((p) => clamp(p - 1 / 12, 0, 1))
      e.preventDefault()
    } else if (e.key === "Enter" || e.key === " ") {
      fillWholeYear()
      e.preventDefault()
    }
  }

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }

  const handleClose = () => native.postNativeEvent("close")

  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? "Start 7 days free"
        : "Keep my year evergreen"
      : "Renew monthly"

  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  const statusLine = evergreen
    ? "Evergreen — your whole year is kept green"
    : plan === "monthly"
      ? "One month green at a time — renews monthly"
      : "Turn the season around to green the whole year"

  return (
    <div className={styles.page}>
      {/* faint almanac paper texture + a soft seasonal wash */}
      <div className={styles.paper} aria-hidden />
      <div className={styles.wash} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <svg viewBox="0 0 24 24" width={17} height={17} aria-hidden>
            <path
              d="M12 2l7 3v6c0 5-3 8-7 11-4-3-7-6-7-11V5l7-3z"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinejoin="round"
            />
            <path
              d="M12 8c-2.6 0-4 1.4-4 4 1.8 0 3-.7 3.8-2.1C11 12 10 13.4 9.6 15c2.6 0 4.4-1.7 4.4-4.4 0-.9 0-1.7-.2-2.6z"
              fill="currentColor"
            />
          </svg>
          <span>Private Photo Vault</span>
          <span className={styles.premiumPip}>Premium</span>
        </div>
        <h1 className={styles.title}>Keep your year evergreen</h1>
        <p className={styles.sub}>
          Your memories, tended all year — automatically backed up, private, and
          in sync on every device.
        </p>
      </header>

      {/* ── The Wheel of the Year ─────────────────────────────────────── */}
      <div className={styles.dialArea}>
        <div
          ref={wrapRef}
          className={styles.dial}
          role="slider"
          tabIndex={0}
          aria-label="Turn the season hand to green the whole year"
          aria-valuemin={1}
          aria-valuemax={12}
          aria-valuenow={monthsInLeaf}
          aria-valuetext={`${monthsInLeaf} of 12 months in leaf`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          data-monthly={plan === "monthly"}
        >
          <svg
            className={styles.wheelSvg}
            viewBox={`0 0 ${BOX} ${BOX}`}
            width="100%"
            height="100%"
          >
            {/* the bare vine (the year, untended) */}
            <circle
              cx={C}
              cy={C}
              r={RING_R}
              className={styles.vineBare}
              fill="none"
            />
            {/* the tended, evergreen vine grown so far */}
            <circle
              cx={C}
              cy={C}
              r={RING_R}
              className={styles.vineGreen}
              fill="none"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - arcFraction)}
              transform={`rotate(-90 ${C} ${C})`}
            />

            {/* central tree — its canopy fills in as the year greens */}
            <g className={styles.tree}>
              <path
                d={`M${C} ${C + 44} L${C} ${C - 6}`}
                className={styles.trunk}
                fill="none"
              />
              <path
                d={`M${C} ${C + 8} q-16 -6 -24 -20 M${C} ${C + 2} q16 -6 24 -22 M${C} ${C - 6} q-12 -8 -14 -22 M${C} ${C - 6} q12 -8 16 -20`}
                className={styles.branches}
                fill="none"
              />
              <g
                className={styles.canopy}
                style={{ opacity: 0.35 + 0.65 * (monthsInLeaf / 12) }}
              >
                <circle cx={C} cy={C - 26} r={20} />
                <circle cx={C - 17} cy={C - 12} r={15} />
                <circle cx={C + 17} cy={C - 12} r={15} />
                <circle cx={C} cy={C - 6} r={13} />
              </g>
            </g>

            {/* the season hand */}
            <g
              className={styles.hand}
              transform={`rotate(${handAngle} ${C} ${C})`}
            >
              <line
                x1={C}
                y1={C}
                x2={C}
                y2={C - (RING_R - 26)}
                className={styles.handStem}
              />
              <g transform={`translate(${C} ${C - (RING_R - 26)})`}>
                <circle r={7.5} className={styles.handBud} />
                <circle r={3} className={styles.handBudCore} />
              </g>
            </g>

            {/* the closing knot at 12 o'clock — ties when the year is whole */}
            <g
              transform={`translate(${C} ${C - RING_R})`}
              className={`${styles.knot} ${evergreen ? styles.knotTied : ""}`}
            >
              <circle r={9} className={styles.knotRing} fill="none" />
              <g className={styles.knotTick}>
                <Tick size={11} />
              </g>
            </g>
          </svg>

          {/* month photo-leaves, positioned on the ring */}
          {MONTHS.map((mo, i) => {
            const p = polar(nodeAngle(i), NODE_R)
            const leafed = i < monthsInLeaf
            return (
              <button
                key={mo.m}
                className={`${styles.node} ${leafed ? styles.nodeLeafed : ""}`}
                style={{
                  left: `${(p.x / BOX) * 100}%`,
                  top: `${(p.y / BOX) * 100}%`,
                }}
                onClick={() => goToMonth(i)}
                tabIndex={-1}
                aria-label={`${mo.m} — ${mo.tag}${leafed ? ", in leaf" : ""}`}
              >
                <span className={styles.sprig} aria-hidden>
                  <Leaf scale={1.1} />
                </span>
                <span
                  className={styles.photo}
                  style={{ backgroundImage: `url(${mo.src})` }}
                />
                <span className={styles.tended} aria-hidden>
                  <Tick size={10} />
                </span>
                <span className={styles.monthLbl}>{mo.m}</span>
              </button>
            )
          })}
        </div>

        <div
          className={`${styles.countChip} ${evergreen ? styles.countChipFull : ""}`}
        >
          <span className={styles.chipLeaf} aria-hidden>
            <Leaf scale={0.75} />
          </span>
          <b>{monthsInLeaf}</b> / 12 months in leaf
        </div>

        <p className={styles.status} aria-live="polite">
          {statusLine}
        </p>
        {plan === "yearly" && !evergreen && (
          <button className={styles.fillAll} onClick={fillWholeYear}>
            Bring the whole year into leaf
          </button>
        )}
      </div>

      {/* what "staying green" actually means (real Premium bundle) */}
      <ul className={styles.keeps}>
        {[
          "Automatic full-resolution backup",
          "Private & encrypted",
          "Organised & in sync everywhere",
        ].map((t) => (
          <li key={t}>
            <span className={styles.keepLeaf} aria-hidden>
              <Leaf scale={0.8} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── Plan choice — yearly featured, monthly small & secondary ──── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planSel : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planLeaf} aria-hidden>
            <Leaf scale={1} />
          </span>
          <span className={styles.planMain}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.planNote}>
              Evergreen · kept green all year
            </span>
          </span>
          <span className={styles.planPrice}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/yr</span>
            <span className={styles.planSmall}>
              {yearlyPerMonth}/mo · save {savingsPct}%
            </span>
          </span>
          {savingsPct > 0 && (
            <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
          )}
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planMonthSel : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        Perennial — your library is always yours. Nothing is ever deleted;
        renewing simply keeps it tended.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default EvergreenPaywall
